/**
 * PortalSidebar - Portal wrapper around AppSidebar from @ecgrid/ecg-react-components
 *
 * Handles data fetching, icon mapping, and navigation. Mounts NetworkSelector
 * inside the AppSidebar via slots.logo so it stays sticky with the sidebar
 * (otherwise it would scroll out of sync with the logo). Visible only when
 * the sidebar is expanded.
 *
 * Usage:
 *   <PortalSidebar isOpen={sidebarOpen} />
 */

import { AppSidebar, cn } from '@/components/ui';
import React from 'react';
import { NetworkSelector } from '../NetworkSelector';
import { usePortalSidebar } from './PortalSidebar.hook';

// ============================================================================
// Loading Skeleton
// ============================================================================

const SidebarSkeleton: React.FC = () => (
  <div className="flex flex-col gap-3 p-4">
    {/* Logo placeholder */}
    <div className="h-8 w-24 bg-muted animate-pulse rounded mb-4" />
    {/* Menu item placeholders */}
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="flex items-center gap-3">
        <div className="h-5 w-5 bg-muted animate-pulse rounded" />
        <div className="h-4 flex-1 bg-muted animate-pulse rounded" />
      </div>
    ))}
  </div>
);

// ============================================================================
// Component
// ============================================================================

interface PortalSidebarProps {
  isOpen: boolean;
  expandedWidth?: string;
}

// AppSidebar's logo wrapper is fixed at h-16 (4rem) and the menu's ScrollArea
// uses h-[calc(100vh-4rem)]. We render NetworkSelector inside slots.logo with
// absolute positioning so it escapes the h-16 box yet stays inside the sticky
// <aside>; menu nav is shifted down via [&_nav]:pt to make room.
export const PortalSidebar: React.FC<PortalSidebarProps> = ({
  isOpen,
  expandedWidth = '400px',
}) => {
  const { mappedMenuItems, logo, collapsedLogo, loading, activePath, handleMenuItemClick } =
    usePortalSidebar();

  if (loading) {
    return <SidebarSkeleton />;
  }

  const logoSrc = isOpen ? logo : collapsedLogo || logo;

  return (
    <AppSidebar
      menuItems={mappedMenuItems}
      isOpen={isOpen}
      activePath={activePath}
      onMenuItemClick={handleMenuItemClick}
      expandedWidth={expandedWidth}
      className={cn(isOpen && '[&_nav]:!mt-0 [&_nav]:pt-[2.75rem]')}
      slots={{
        logo: (
          <>
            <img
              src={logoSrc}
              alt="Logo"
              className={cn('mx-auto', isOpen ? 'max-h-10 max-w-[150px]' : 'h-12 w-12')}
            />
            {isOpen && (
              <div className="absolute left-0 right-0 top-16 z-10 px-3">
                <NetworkSelector />
              </div>
            )}
          </>
        ),
      }}
    />
  );
};

PortalSidebar.displayName = 'PortalSidebar';
